'use client';

import type { EditTarget, MenuItemEditTarget } from '@web-studio/shared-schemas';
import { LAYOUT_COMPONENT_TYPE } from '@web-studio/shared-schemas';
import { useEffect, useRef } from 'react';
import { EditableContent } from '../../../edit/EditableContent';
import { pickBlockField } from '../../../lib/localized';
import { isDrinkLikeMenuCategoryTitle } from '../../../lib/menuCategoryKind';
import { parseMenuColumns, type MenuColumn, type MenuItem } from '../../../lib/parseMenuColumns';
import { pickChrome } from '../../../lib/siteChromeCopy';
import type { BlockRenderProps } from '../../../types';
import { MenuItemDietaryTags, MenuItemName, MenuItemOrderCode, MenuItemPriceSlot } from '../MenuItemTags';
import { MenuLegalLegend } from '../MenuLegalLegend';
import {
  MENU_FLOW_CHIP_SCROLL_THRESHOLD,
  resolveFullMenuDietaryMode,
} from '../fullMenuDietary';
import { bindMenuFlowChipRail } from './bindMenuFlowChipRail';
import { revealMenuFlowPanel } from './revealMenuFlowPanel';

function menuItemHasContent(item: MenuItem): boolean {
  return Boolean(item.name.trim() || item.price.trim() || item.description.trim());
}

function isMenuItemActive(
  activeEditTarget: MenuItemEditTarget | null | undefined,
  columnIndex: number,
  itemIndex: number,
): boolean {
  return (
    activeEditTarget?.kind === 'menu-item' &&
    activeEditTarget.columnIndex === columnIndex &&
    activeEditTarget.itemIndex === itemIndex
  );
}

function isMenuColumnActive(
  activeEditTarget: EditTarget | null | undefined,
  columnIndex: number,
): boolean {
  return activeEditTarget?.kind === 'menu-column' && activeEditTarget.columnIndex === columnIndex;
}

function pageHasDrinksList(allBlocks: BlockRenderProps['allBlocks']): boolean {
  return Boolean(
    allBlocks?.some((block) => block.componentType === LAYOUT_COMPONENT_TYPE.drinksListStatic),
  );
}

function resolveMenuFlowColumns(
  menuDataRaw: string,
  stripDrinkCategories: boolean,
): MenuColumn[] {
  const columns = parseMenuColumns(menuDataRaw);
  if (!stripDrinkCategories) {
    return columns;
  }
  return columns.filter((column) => !isDrinkLikeMenuCategoryTitle(column.title));
}

function MenuFlowPanel({
  column,
  columnIndex,
  panelId,
  labelledBy,
  panelAttr,
  editable,
  activeEditTarget,
  locale,
  dietaryInline,
}: {
  column: MenuColumn;
  columnIndex: number;
  panelId: string;
  labelledBy: string;
  panelAttr: string;
  editable?: boolean;
  activeEditTarget?: BlockRenderProps['activeEditTarget'];
  locale: string;
  dietaryInline: boolean;
}) {
  return (
    <div
      className="ws-menu-flow__panel"
      id={panelId}
      data-panel={panelAttr}
      role="region"
      aria-labelledby={labelledBy}
    >
      <ul className="ws-menu-flow__list">
        {column.items.map((item, itemIndex) => {
          if (!menuItemHasContent(item)) {
            return null;
          }
          const active = isMenuItemActive(
            activeEditTarget?.kind === 'menu-item' ? activeEditTarget : null,
            columnIndex,
            itemIndex,
          );
          return (
            <li key={itemIndex}>
              <article
                className={['ws-menu__item', active ? 'ws-edit-child--active' : '']
                  .filter(Boolean)
                  .join(' ')}
                {...(editable
                  ? {
                      'data-edit-child': 'menu-item' as const,
                      'data-column-index': String(columnIndex),
                      'data-item-index': String(itemIndex),
                    }
                  : {})}
              >
                <div className="ws-menu__item-row">
                  <MenuItemOrderCode code={item.code} />
                  <div className="ws-menu__item-copy">
                    <div className="ws-menu__item-primary">
                      <MenuItemName item={item} locale={locale} showOrderCode={false} />
                      {dietaryInline ? (
                        <MenuItemDietaryTags
                          item={item}
                          locale={locale}
                          iconsOnly
                          placement="inline"
                        />
                      ) : null}
                    </div>
                    {item.description ? (
                      <p className="ws-menu__item-desc">{item.description}</p>
                    ) : null}
                    {!dietaryInline ? <MenuItemDietaryTags item={item} locale={locale} /> : null}
                  </div>
                  <MenuItemPriceSlot price={item.price} />
                </div>
              </article>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * Category chips Speisekarte (also Dynamic_Menu_Flow type).
 * On phone the rail is one scrolling row at any category count. The bar sticks
 * under sticky nav (and utility when present) — including Overlay/Quiet/Whisper
 * panel mode on Speisekarte. Gutter padding stays put while the chips scroll.
 * Wider sites wrap + center until MENU_FLOW_CHIP_SCROLL_THRESHOLD, then scroll.
 * Panels use :has() so the tab row can scroll independently of the item list.
 * Switching a chip while the bar is stuck scrolls the new list clear of the bar.
 */
export function DynamicMenuFlow({
  block,
  locale,
  isActive,
  activeEditTarget,
  editable,
  allBlocks,
}: BlockRenderProps) {
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const sectionSubtitle = pickBlockField(block.content, 'sectionSubtitle', locale);
  const menuDataRaw = pickBlockField(block.content, 'menuData', locale);
  const columns = resolveMenuFlowColumns(menuDataRaw, pageHasDrinksList(allBlocks));
  const groupName = `menu-flow-${block.blockId}`;
  const chromeId = `${groupName}-chrome`;
  const target = isActive ? activeEditTarget : null;
  const categoriesLabel = sectionTitle || pickChrome('menuCategories', locale);
  const chipsOverflow = columns.length > MENU_FLOW_CHIP_SCROLL_THRESHOLD;
  const dietaryMode = resolveFullMenuDietaryMode(block.registryBlockKey, block.componentType);
  const dietaryInline = dietaryMode === 'iconsInline';
  const railRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const rail = railRef.current;
    if (!rail) {
      return;
    }
    return bindMenuFlowChipRail(rail, { editable });
  }, [editable, columns.length]);

  useEffect(() => {
    const rail = railRef.current;
    const tabs = tabsRef.current;
    if (!rail || !tabs) {
      return;
    }
    const syncScrollable = () => {
      const scrollable = rail.scrollWidth > rail.clientWidth + 2;
      if (scrollable) {
        tabs.setAttribute('data-chips-scrollable', 'true');
      } else {
        tabs.removeAttribute('data-chips-scrollable');
      }
    };
    syncScrollable();
    const observer = new ResizeObserver(syncScrollable);
    observer.observe(rail);
    return () => observer.disconnect();
  }, [columns.length, chipsOverflow]);

  const panelShowCss = columns
    .map(
      (_, index) =>
        `#${chromeId}:has(#${groupName}-tab-${index}:checked) #${groupName}-panel-${index}{display:block}`,
    )
    .join('');

  return (
    <section
      className={['ws-menu-flow', isActive ? 'ws-block--active' : ''].filter(Boolean).join(' ')}
      data-block-id={block.blockId}
      data-component={block.componentType}
      data-registry-key={block.registryBlockKey}
      id="menu"
    >
      {panelShowCss ? <style>{panelShowCss}</style> : null}
      <div className="ws-menu-flow__intro">
        <EditableContent
          as="h2"
          fieldKey="sectionTitle"
          editable={editable}
          activeEditTarget={target}
          className="ws-menu-flow__title"
        >
          {sectionTitle}
        </EditableContent>
        <EditableContent
          fieldKey="sectionSubtitle"
          editable={editable}
          activeEditTarget={target}
          className="ws-menu-flow__subtitle"
        >
          {sectionSubtitle}
        </EditableContent>
        {sectionTitle || sectionSubtitle ? (
          <div className="ws-menu-flow__rule" aria-hidden="true" />
        ) : null}
      </div>
      <div className="ws-menu-flow__body">
        {columns.length > 0 ? (
          <div className="ws-menu-flow__chrome" id={chromeId}>
            <div
              className="ws-menu-flow__tabs"
              ref={tabsRef}
              role="radiogroup"
              aria-label={categoriesLabel}
              data-chips-overflow={chipsOverflow ? 'true' : undefined}
              data-chip-count={columns.length}
            >
              <div className="ws-menu-flow__rail" ref={railRef}>
                {columns.map((col, index) => {
                  const columnActive = isMenuColumnActive(target, index);
                  const tabId = `${groupName}-label-${index}`;
                  return (
                    <span key={`tab-${index}`} className="ws-menu-flow__tab-wrap">
                      <input
                        type="radio"
                        name={groupName}
                        id={`${groupName}-tab-${index}`}
                        className="ws-menu-flow__radio"
                        defaultChecked={index === 0}
                        onChange={(event) => {
                          revealMenuFlowPanel(event.currentTarget);
                        }}
                      />
                      <label
                        id={tabId}
                        htmlFor={`${groupName}-tab-${index}`}
                        className={[
                          'ws-menu-flow__tab',
                          columnActive ? 'ws-edit-child--active' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        {...(editable
                          ? {
                              'data-edit-child': 'menu-column' as const,
                              'data-column-index': String(index),
                            }
                          : {})}
                      >
                        {col.title.trim() || `Category ${index + 1}`}
                      </label>
                    </span>
                  );
                })}
              </div>
            </div>
            <div className="ws-menu-flow__panels">
              {columns.map((col, index) => (
                <MenuFlowPanel
                  key={`panel-${index}`}
                  column={col}
                  columnIndex={index}
                  panelId={`${groupName}-panel-${index}`}
                  labelledBy={`${groupName}-label-${index}`}
                  panelAttr={String(index)}
                  editable={editable}
                  activeEditTarget={target}
                  locale={locale}
                  dietaryInline={dietaryInline}
                />
              ))}
            </div>
          </div>
        ) : null}
        <MenuLegalLegend columns={columns} locale={locale} />
      </div>
    </section>
  );
}
